/**
 * RfiDetail — paper Form F-1.6 layout + lifecycle actions for AISC §1.7.0 Phase 2.
 */

import React, { useEffect, useState } from "react";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Alert, Chip, Divider,
  TextField, Breadcrumbs, Link as MuiLink, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, EditRounded, SendRounded,
  ReplyRounded, CheckCircleOutlineRounded, CancelRounded,
} from "@mui/icons-material";

import {
  getRfi, sendRfi, answerRfi, closeRfi, cancelRfi, allowedRfiTransitions,
} from "../../utils/rfisApi";

const STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#6c757d" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  ANSWERED:  { bg: "#d1e7dd", color: "#0a3622" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};

export default function RfiDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [rfi, setRfi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [openAnswer, setOpenAnswer] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [answeredByParty, setAnsweredByParty] = useState("");
  const [answerDocId, setAnswerDocId] = useState("");

  const [openCancel, setOpenCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const refresh = () => {
    setLoading(true);
    getRfi(id)
      .then(setRfi)
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id]);

  const fireAction = async (fn, ...args) => {
    setBusy(true); setError(null);
    try { await fn(id, ...args); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setBusy(false); }
  };

  const handleAnswer = async () => {
    if (!answerText && !answerDocId) {
      setError("Answer text or doc ID required");
      return;
    }
    setBusy(true); setError(null);
    try {
      await answerRfi(id, {
        answer_text: answerText || null,
        answered_by_party: answeredByParty || null,
        answer_doc_id: answerDocId ? Number(answerDocId) : null,
      });
      setOpenAnswer(false); setAnswerText(""); setAnsweredByParty(""); setAnswerDocId("");
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setBusy(false); }
  };

  if (loading) return <Container sx={{ py: 4 }}><Typography>Loading…</Typography></Container>;
  if (!rfi) return <Container sx={{ py: 4 }}><Alert severity="error">RFI not found.</Alert></Container>;

  const colors = STATUS_COLOR[rfi.status] || { bg: "#e9ecef", color: "#495057" };
  const next = allowedRfiTransitions(rfi.status);

  return (
    <>
      <Helmet><title>{rfi.rfi_number} | SFM</title></Helmet>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <MuiLink href="/rfis" color="inherit" underline="hover">RFIs</MuiLink>
          <Typography color="text.primary">{rfi.rfi_number}</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button startIcon={<ArrowBackRounded />} onClick={() => navigate("/rfis")}>Back</Button>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{rfi.rfi_number}</Typography>
            <Chip label={prettyStatus(rfi.status)} sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            {rfi.status === "DRAFT" && (
              <Button startIcon={<EditRounded />} onClick={() => navigate(`/rfis/${id}/edit`)}>Edit</Button>
            )}
            {next.includes("SENT") && (
              <Button startIcon={<SendRounded />} variant="contained" disabled={busy}
                onClick={() => fireAction(sendRfi)}>Send</Button>
            )}
            {next.includes("ANSWERED") && (
              <Button startIcon={<ReplyRounded />} variant="contained" color="success" disabled={busy}
                onClick={() => setOpenAnswer(true)}>Record answer</Button>
            )}
            {next.includes("CLOSED") && (
              <Button startIcon={<CheckCircleOutlineRounded />} variant="contained" disabled={busy}
                onClick={() => fireAction(closeRfi, "")}>Close</Button>
            )}
            {next.includes("CANCELLED") && (
              <Button startIcon={<CancelRounded />} color="error" disabled={busy}
                onClick={() => setOpenCancel(true)}>Cancel</Button>
            )}
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 3 }}>
          {/* Paper-form layout — Form F-1.6 mirror */}
          <Box sx={{ borderBottom: "1px solid #dee2e6", pb: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ color: "#6c757d", letterSpacing: 1 }}>FORM F-1.6 / RFI</Typography>
            <Typography variant="h6">{rfi.rfi_number}</Typography>
            <Typography variant="body2" sx={{ color: "#6c757d" }}>
              Job #{rfi.job_id} · Created {rfi.created_at ? new Date(rfi.created_at).toISOString().slice(0, 10) : "—"}
            </Typography>
          </Box>

          {(rfi.reference_drawing_id || rfi.reference_drawing_text) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Reference drawing</Typography>
              <Typography>
                {rfi.reference_drawing_id && <span>Doc #{rfi.reference_drawing_id} · </span>}
                {rfi.reference_drawing_text}
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Requested of</Typography>
            <Typography>{rfi.requested_of_party}</Typography>
            {rfi.requested_of_email && <Typography variant="body2" sx={{ color: "#6c757d" }}>{rfi.requested_of_email}</Typography>}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Information requested</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap", py: 1 }}>{rfi.information_requested}</Typography>
          </Box>

          {rfi.suggestions && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>A3 suggestions</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", py: 1, fontStyle: "italic" }}>{rfi.suggestions}</Typography>
            </Box>
          )}

          {(rfi.answer_text || rfi.answer_doc_id) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "#0a3622", fontWeight: 600 }}>Answer received</Typography>
                {rfi.answered_at && (
                  <Typography variant="caption" sx={{ color: "#6c757d" }}>
                    {new Date(rfi.answered_at).toISOString().slice(0, 19).replace("T", " ")} UTC
                    {rfi.answered_by_party && ` · ${rfi.answered_by_party}`}
                  </Typography>
                )}
                {rfi.answer_text && <Typography sx={{ whiteSpace: "pre-wrap", py: 1 }}>{rfi.answer_text}</Typography>}
                {rfi.answer_doc_id && (
                  <Typography variant="body2" sx={{ color: "#6c757d" }}>Signed doc: #{rfi.answer_doc_id}</Typography>
                )}
              </Box>
            </>
          )}

          {rfi.closure_notes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Closure notes</Typography>
                <Typography sx={{ whiteSpace: "pre-wrap", py: 1 }}>{rfi.closure_notes}</Typography>
              </Box>
            </>
          )}

          {rfi.cancellation_reason && (
            <>
              <Divider sx={{ my: 2 }} />
              <Alert severity="error">
                <Typography variant="body2"><strong>Cancelled:</strong> {rfi.cancellation_reason}</Typography>
              </Alert>
            </>
          )}
        </Paper>

        <Box sx={{ mt: 3, color: "#6c757d", fontSize: "0.8rem" }}>
          <Typography variant="caption">Tip: Use Cmd+P / Ctrl+P to print this page as PDF (interim until Phase 2.5 server-side PDF lands).</Typography>
        </Box>
      </Container>

      <Dialog open={openAnswer} onClose={() => setOpenAnswer(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record RFI answer</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={5} label="Answer text" value={answerText}
            onChange={(e) => setAnswerText(e.target.value)} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth label="Answered by (party)" value={answeredByParty}
            onChange={(e) => setAnsweredByParty(e.target.value)} sx={{ mb: 2 }}
            placeholder="e.g., William Dube, NEI" />
          <TextField fullWidth label="Signed answer doc ID (optional)" type="number"
            value={answerDocId} onChange={(e) => setAnswerDocId(e.target.value)}
            helperText="Either text OR doc ID required" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnswer(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleAnswer} disabled={busy}>
            {busy ? "Saving…" : "Record answer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCancel} onClose={() => setOpenCancel(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel RFI</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Cancellation reason (min 4 chars)" required
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancel(false)} disabled={busy}>Back</Button>
          <Button variant="contained" color="error" onClick={async () => {
            await fireAction(cancelRfi, cancelReason);
            setOpenCancel(false); setCancelReason("");
          }} disabled={busy || cancelReason.length < 4}>Cancel RFI</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
